import type { FC } from 'react';
import { InlineEditableText } from '@components/InlineEditableText';
import InputSelect from '@components/InputSelect';
import Tooltip from '@components/Tooltip';
import type { UIInputValue } from '@components/types';
import { responsiveTruncateClasses } from '@ContractBuilder/contract-builder-header/classes';
import { EntityField } from '@ContractBuilder/contract-builder-header/constants';
import type { OnSaveFn } from '@ContractBuilder/contract-builder-header/modules/entity-details/hooks/use-editable-field';
import { useDatapointsStore } from '@ContractBuilder/store/datapoints.store';
import type { EntityData } from '@domain/types/entity';
import { LocationMarkerIcon } from '@heroicons/react/solid';
import { ALL_CLASSES_OF_BUSINESS } from '@root/helpers/constants';

import { useEditableField } from '../hooks';

interface EditableClassOfBusinessFieldProps {
  entity: EntityData;
  hasOperationsDisabled: boolean;
  onSave: OnSaveFn<string | undefined>;
}

export const EditableClassOfBusinessField: FC<EditableClassOfBusinessFieldProps> = ({
  entity,
  hasOperationsDisabled,
  onSave,
}) => {
  const hasUnsavedChanges = useDatapointsStore((state) => state.isDirty);
  const isDisabled = hasOperationsDisabled || hasUnsavedChanges;

  // Unset stays undefined, so confirming without a choice sends no class_of_business instead of saving ''.
  const { onCancel, onChange, onClick, onUpdate, state } = useEditableField<string | undefined>({
    defaultValue: entity.class_of_business,
    name: EntityField.ClassOfBusiness,
    onSave,
  });

  return (
    <div className="flex items-center gap-1">
      <Tooltip content="Class of Business">
        <span>
          <LocationMarkerIcon className="h-4 w-4" />
        </span>
      </Tooltip>
      <InlineEditableText
        editor={
          <InputSelect
            dataTestId="select:editable-class-of-business"
            value={state.value as UIInputValue}
            options={ALL_CLASSES_OF_BUSINESS.map((label) => ({ name: label, value: label }))}
            onChange={(value) => onChange(value ?? '')}
            name={EntityField.ClassOfBusiness}
            isDisabled={false}
            size="sm"
            menuOuterClassName="min-w-fit"
          />
        }
        editMode={state.isEditing}
        disabled={isDisabled}
        display={entity.class_of_business || 'No class set'}
        onClick={onClick}
        onCancel={onCancel}
        onConfirm={onUpdate}
        confirmDisabled={hasUnsavedChanges}
        tooltipContent={!isDisabled ? 'Edit class of business' : undefined}
        withEditIcon={false}
        className={responsiveTruncateClasses([
          'rounded-sm border border-transparent px-1 py-0.5',
          !isDisabled ? 'hover:border-gray-300' : '',
        ])}
      />
    </div>
  );
};
